import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/browser";
import { signOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import ChildContext from "../context/child";
import { DESKTOP_VERSION_FOR_GEOFENCING, DESKTOP_VERSION_FOR_GPS, compareDesktopVersion, deviceType } from "../util/helper";
import { useMe } from "@/util/me-context";
import { ChildResponse, ChildSubscriptionType, DeviceType, DeviceSubscriptionType, DeviceTypeEnum } from "@/openapi";
import { useChild } from "@/util/child-helper";
import { featureVersion } from "@/App";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleWindowSizeChange = () => {
      setIsMobile(window.innerWidth <= 1023);
    };

    handleWindowSizeChange();

    window.addEventListener("resize", handleWindowSizeChange);

    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  return isMobile;
};

const useLogout = () => {
  const navigate = useNavigate();

  const logOut = useCallback(async () => {
    Sentry.getGlobalScope().setUser(null);
    await signOut();
    if (deviceType === DeviceType.Mobile) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "logout" }));
    }
    navigate("/welcome", { replace: true });
  }, [navigate]);

  return logOut;
};

const useCountHeight = () => {
  const HEIGHT_OF_MOBILE_NOTIFICATION = 55.5; // heigh of notification and header title
  const HEIGHT_OF_HEADER = 51;
  const isMobile = useIsMobile();
  const { child } = useContext(ChildContext);
  const hasAnyDeviceToken = child?.deviceInfos.some(deviceInfo => !!deviceInfo.fcmDeviceToken);
  const hasAnyPhoneNumber = child?.deviceInfos.some(deviceInfo => !!deviceInfo.phoneNumber);

  const showVerifyBanner = () => {
    if (hasAnyDeviceToken && !child?.configuration.allowGroupMessaging) return true;
    return false;
  };

  const currentHeight = window.innerHeight;
  
  let height;

  if (currentHeight === null) {
    height =
      (showVerifyBanner() || (hasAnyDeviceToken && !hasAnyPhoneNumber)) && isMobile
        ? window.innerWidth - (HEIGHT_OF_MOBILE_NOTIFICATION + HEIGHT_OF_HEADER)
        : window.innerWidth - HEIGHT_OF_HEADER;
  } else {
    height =
      (showVerifyBanner() || (hasAnyDeviceToken && !hasAnyPhoneNumber)) && isMobile
        ? currentHeight - (HEIGHT_OF_MOBILE_NOTIFICATION + HEIGHT_OF_HEADER)
        : currentHeight - HEIGHT_OF_HEADER;
  }

  return height;
};

const useIsGPSBetaUser = () => {
  return true;
};

const useIsGpsCapable = () => {
  const { child } = useContext(ChildContext);
  return useMemo(() => {
    if (child && child.deviceInfos && child.deviceInfos.length > 0 && child.deviceInfos[0].desktopVersion) {
      return compareDesktopVersion(child.deviceInfos[0].desktopVersion, DESKTOP_VERSION_FOR_GPS);
    } else {
      return false;
    }
  }, [child]);
};

const useIsGeoFencingCapable = () => {
  const { child } = useContext(ChildContext);
  return useMemo(() => {
    if (child && child.deviceInfos && child.deviceInfos.length > 0 && child.deviceInfos[0].desktopVersion) {
      return compareDesktopVersion(child.deviceInfos[0].desktopVersion, DESKTOP_VERSION_FOR_GEOFENCING);
    } else {
      return false;
    }
  }, [child]);
};

const useIsWatchChild = () => {
  const child = useChild();
  return useMemo(() => {
    const deviceInfos = child?.deviceInfos;
    if (deviceInfos && deviceInfos.length > 0) {
      return deviceInfos[0].deviceType === DeviceTypeEnum.Watch;
    }
    return child?.subscriptionTypes?.[0] === ChildSubscriptionType.Watch;
  },[child]);
};

const useIsPinwheelHomeChild = (child: ChildResponse | null) => {
  return useMemo(() => {
    return (child?.subscriptionTypes?.includes(ChildSubscriptionType.HomeFree) || child?.subscriptionTypes?.includes(ChildSubscriptionType.HomeFf) || child?.subscriptionTypes?.includes(ChildSubscriptionType.HomeUnlimited) || child?.subscriptionTypes?.includes(ChildSubscriptionType.HomeGlobal)) ?? false;
  }, [child]);
};

const useChildHasPwCode = (child: ChildResponse | null) => {
  return useMemo(() => {
    return !!child?.deviceInfos?.[0]?.pwCode;
  }, [child]);
};

const useIsWatchSubBypassCapable = () => {
  const user = useMe();
  return useMemo(() => user.cognitoGroups.includes("watchSubBypass"), [user]);
}

const whatsNewLSKey = "whatsNewLastVersion";
const useWhatsNewState = () => {
  const [showNotification, setShowNotification] = useState(featureVersion > (Number(localStorage.getItem(whatsNewLSKey)) || 0));
  const onLinkClicked = useCallback(() => {
    setShowNotification(false);
    localStorage.setItem(whatsNewLSKey, featureVersion.toString());
  }, [])

  return {showNotification, onLinkClicked}
}

const usePinwheelHomeEnabled = (allowedDeviceSubTypes?: DeviceSubscriptionType[]) => {
  return useMemo(
    () => (allowedDeviceSubTypes?.includes(DeviceSubscriptionType.Home)) ?? false,
    [allowedDeviceSubTypes],
  );
};

export {
  useIsMobile,
  useLogout,
  useCountHeight,
  useIsGpsCapable,
  useIsGPSBetaUser,
  useIsGeoFencingCapable,
  useIsWatchChild,
  useIsPinwheelHomeChild,
  useChildHasPwCode,
  useIsWatchSubBypassCapable,
  useWhatsNewState,
  usePinwheelHomeEnabled
};